<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
          <h1>Repeat Reason Report</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-card>
          <div slot="header">
              <strong>Report Options</strong>
          </div>
          <div class="form-container">
            <b-row>
                <b-col sm="4">
                    <b-form-group>
                        <label for="company">Company</label>
                        <b-form-select id="company" v-if="companiesLoaded"  v-model="reportParams.companyId"
                            :plain="true"
                            :options="companies"
                            :disabled="!isMinimumSuperAdmin">
                        </b-form-select>
                        <div v-if="!companiesLoaded" >
                            <i class="fa fa-spin fa-spinner"></i>
                        </div>
                    </b-form-group>
                </b-col>
                <b-col sm="4">
                    <b-form-group>
                        <label for="area">Area</label>
                        <b-form-select id="area" v-if="areasLoaded"  v-model="reportParams.areaId"
                            :plain="true"
                            :options="areas"
                            :disabled="!isMinimumCompanyUser">
                        </b-form-select>
                        <div v-if="!areasLoaded" >
                          <i class="fa fa-spin fa-spinner"></i> Waiting for company selection
                        </div>
                    </b-form-group>
                </b-col>
                <b-col sm="4">
                    <b-form-group>
                        <label for="site">Site</label>
                        <b-form-select id="site" v-if="sitesLoaded"  v-model="reportParams.siteId"
                            :plain="true"
                            :options="sites"
                            :disabled="!isMinimumAreaUser">
                        </b-form-select>
                        <div v-if="!sitesLoaded" >
                          <i class="fa fa-spin fa-spinner"></i> Waiting for area selection
                        </div>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
              <b-col sm="3">
                <b-form-group>
                  <label for="reportCountryType">Country</label>
                  <b-form-select id="reportCountryType"  v-model="reportParams.reportCountryType"
                      :plain="true"
                      :options="reportCountryTypes">
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col sm="3">
                  <b-form-group>
                      <label for="wip-number">WIP Number</label>
                      <b-form-input type="text" id="wip-number" v-model="reportParams.wipNumber"></b-form-input>
                  </b-form-group>
              </b-col>
              <b-col sm="3">
                  <b-form-group>
                      <label for="dateFrom">Date From</label>
                      <b-form-input type="date" id="dateFrom" v-model="reportParams.dateFrom"></b-form-input>
                  </b-form-group>
              </b-col>
              <b-col sm="3">
                  <b-form-group>
                      <label for="dateTo">Date To</label>
                      <b-form-input type="date" id="dateTo" v-model="reportParams.dateTo"></b-form-input>
                  </b-form-group>
              </b-col>
            </b-row>
            <b-alert class="mt-2" v-if="invalidDateRange" variant="warning" show>
              Selected dates are than 3 months apart. You may still run the report to screen but there is a higher chance of timeout. Please consider using the background report option for larger date ranges.
            </b-alert>
            <b-alert class="mt-2" v-if="dateInFutureError" variant="danger" show>
              The 'Date From' must be before or the same day as the 'Date To'. Please change the dates to continue.
            </b-alert>
            <hr />
            <b-row>
              <b-col cols="12" sm="6" md="6">
                <p>
                  <b-button v-on:click="runReport" variant="primary" :disabled="dateInFutureError">
                    <i class="fa fa-search"></i>&nbsp; Run Report
                  </b-button>
                  &nbsp;
                  <span>Quick report to screen?&nbsp;</span>
                   <b-button 
                        class="toggle-option-button"
                        :variant="getClassForResultsTarget(reportOutput)"
                        @click="setResultsTarget(reportOutput)"
                    >
                        <span v-if="reportOutput == 'screen'">Yes</span><span v-else>No</span>
                    </b-button>&nbsp;
                </p>
              </b-col>
              <b-col cols="12" sm="6" md="6" class="col-right">
                <b-button v-on:click="gotoUserReports" variant="outline-download">
                  Past Reports
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-card v-if="reportFetchStarted">
          <div slot="header">
              <strong>Report Results</strong>
          </div>
          <div v-if="reportOutput == 'screen'">
            <div v-if="!resultsLoaded" class="form-container">
                <LoadingSpinner />
            </div>
            <div v-if="resultsLoaded" class="form-container">
              <b-row>
                <b-col sm="3">
                  <b-form-group
                    label="Filter"
                    label-for="tableFilter"
                    :label-cols="3"
                    :horizontal="true">
                    <b-form-input v-model="tableFilter" type="text" id="tableFilter"></b-form-input>
                  </b-form-group>                                    
                </b-col>
                <b-col sm="3">
                  <b-form-group>
                    <b-button v-on:click="csvExport" variant="primary">
                      <i class="fa fa-download"></i>&nbsp; Export CSV
                    </b-button>
                  </b-form-group>                                    
                </b-col>                         
              </b-row>
              <b-row>
                <b-col cols="12">
                  <div id="customer-list">
                    <!--<c-table :filter="tableFilter" :table-data="contactItems" :fields="tableFields" :perPage="10" hover bordered caption="All Contacts"></c-table>-->
                    <b-table
                      id="results-table"
                      class="datalist-table"
                      selectable
                      hover 
                      bordered
                      select-mode="single"                                            
                      :items="reportItems" 
                      :fields="tableFields"
                      :filter="tableFilter"
                      responsive="sm"
                        >
                    </b-table>
                  </div>
                </b-col>
              </b-row>
            </div>
          </div>
          <div v-if="reportOutput == 'download'">
            <div class="form-container">
              <b-row>
                <b-col>
                  <div v-if="reportBackgroundStatus == 'success'" class="background-report-output">
                    <i class="fa fa-check p-3 fa-4x mr-2 text-success"></i>
                    <div class="h5 text-success mb-0">Report is currently being processed</div>
                  </div>
                  <div class="text-success text-center">
                    Please be aware that this could take upto 1 hour per report request. <br />
                    When the report is ready, an email will be sent to {{ userDetails.email }}. <br />
                    Alternatively, you many check the <b-link class="font-weight-bold text-success" v-bind:href="'/#/reports/user-report'">past reports</b-link> page shortly.
                  </div>
                  <div v-if="reportBackgroundStatus == 'error'" class="background-report-output">
                    <i class="fa fa-times-circle p-3 fa-4x mr-2 text-danger"></i>
                    <div class="h5 text-danger mb-0">There was an error when trying to build the report. Please contact your administrator.</div>
                  </div>            
                </b-col>                      
              </b-row>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import LoadingSpinner from '../../components/loadingSpinner/LoadingSpinner';
import cTable from '../../components/table/Table';
import { Switch as cSwitch } from '@coreui/vue';
import moment from 'moment';

const getHTTPHeaders = () => {
  let userObj = JSON.parse(localStorage.getItem('user'));
  let headerConfig = {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      'Authorization': 'Bearer ' + userObj.token,
    }
  }
  return headerConfig;
}

const getCurrentUser = () => {
    return new Promise((resolve, reject) => {
        let user = JSON.parse(localStorage.getItem('user'));
        if (user != null) {
            resolve(user);
        }
        else {
            reject();
        }
        
    });
}

const getCompaniesList = (($http) => {
  return new Promise((resolve, reject) => {
    let headersObj = getHTTPHeaders();
      $http.get('getCompaniesForSelect', headersObj)
        .then((response) => {
          // let companies = response.data.companies.map((element) => {
          //   let newElement = {
          //     value: element.id,
          //     text: element.company_name
          //   };
          //   return newElement;
          // });
          resolve(response.data.companies);
        })
        .catch ((err) => {
          console.error(err);
          reject(err);
        });
  });
});

const getSiteList = (($http, areaId) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
        $http.get('getSitesForSelect?area_id=' + areaId, headersObj)
            .then((response) => {
                resolve(response.data.sites);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});

const getAreaList = (($http, companyId) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
         $http.get('getAreasForSelect?company_id=' + companyId, headersObj)
            .then((response) => {
                resolve(response.data.areas);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});

const fetchReportData = (($http, reportParams) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
        let reportParamsString = `date_from=${reportParams.dateFrom}&date_to=${reportParams.dateTo}&company_id=${reportParams.companyId}&wip_number=${reportParams.regNumber}`;
        if (reportParams.areaId > 0) {
          reportParamsString += `&area_id=${reportParams.areaId}`;
        }
        if (reportParams.siteId > 0) {
          reportParamsString += `&site_id=${reportParams.siteId}`;
        }
        $http.get('reports/stats/reprint-reason?' + reportParamsString, headersObj)
            .then((response) => {
                resolve(response.data);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});

const fetchBackgroundReportData = (($http, reportParams) => {
    return new Promise((resolve, reject) => {
        let headersObj = getHTTPHeaders();
        let reportParamsString = `date_from=${reportParams.dateFrom}&date_to=${reportParams.dateTo}&company_id=${reportParams.companyId}&wip_number=${reportParams.wipNumber}`;
        if (reportParams.areaId > 0) {
          reportParamsString += `&area_id=${reportParams.areaId}`;
        }
        if (reportParams.siteId > 0) {
          reportParamsString += `&site_id=${reportParams.siteId}`;
        }
        $http.get('reports/stats/background/reprint-reason?' + reportParamsString, headersObj)
            .then((response) => {
                resolve(response.data);
            })
            .catch ((err) => {
                console.error(err);
                reject(err);
            });
    });
});

export default {

  name: 'repeatReasonReport',
  components: {
    LoadingSpinner: LoadingSpinner,
    cTable,
    cSwitch
  },
  data: function () {
    return {
      reportParams: {
        companyId: 0,
        areaId: 0,
        siteId: 0,
        wipNumber: '',
        dateFrom: moment(Date.now()).format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        reportType: 'both',
        reportCountryType: 'uk',
        jobType: 'report'
      },
      invalidDateRange: false,
      dateInFutureError: false,
      quickReport: 0,
      reportOutput: 'screen',
      reportBackgroundStatus: 'processing',
      tableFilter: '',
        tablePerPage: 20,
        tableCurrentPage: 1,
        tableTotalRows: 0,
        tableFields: [
          {key: 'country_type', label: 'Country', sortable: true},
          {key: 'site.site_name', label: 'Site', sortable: true},
          {key: 'supplier_wip_number', label: 'WIP Number', sortable: true},
          {key: 'registration_number', label: 'Reg Number', sortable: true},
          {key: 'reprint_reason', label: 'Repeat Reason', sortable: true},
          {key: 'plate_code', label: 'Plate Type', sortable: true},
          {key: 'template.template_name', label: 'Template', sortable: true},
          {key: 'printed_by_name', label: 'Printed By', sortable: true},
          {key: 'formatted_date', label: 'Print Date', sortable: true},
        ],
      instantRegValue: '',
      reportItems: [],
      reportRowLimit: 0,
      reportRowCount: 0,
      exportReportData: [],
      companies: [],
      areas: [],
      sites: [],
      reportTypes: [
        {'text': 'Both', 'value': 'both'},
        {'text': 'Trade', 'value': 'trade'},
        {'text': 'Retail', 'value': 'retail'}
      ],
      reportCountryTypes: [
        {'text': 'All', 'value': 'all'},
        {'text': 'UK', 'value': 'uk'},
        {'text': 'Eire', 'value': 'eire'}
      ],
      resultsLoaded: false,
      reportFetchStarted: false,
      companiesLoaded: false,
      sitesLoaded: false,
      areasLoaded: false,
      allowRunReport: false,
      userAuthLevel : '',
      userDetails: ''
    }
  },
  created () {
    this.getInitialData();
    //this.setUIForCurrentUser();
  },
  watch: {
    'reportParams.companyId': function(val, oldVal) {
      
      // get the branches for the selected company
      // clear it out first
      this.$data.areas = [];
      getAreaList(this.$http, val)
        .then((areaList) => {
          if (this.$data.userAuthLevel == 'Super Admin' || this.$data.userAuthLevel == 'Company Users') {
            this.$data.areas.push({value:0, text: 'All Areas'});
            this.$data.reportParams.areaId = 0;
            this.$data.reportParams.siteId = 0;
          }
          areaList.map((item) => {
            this.$data.areas.push(item);
          });
          this.$data.areasLoaded = true;
        })
        .catch ((err) => {
          console.error(err);
        }); 
    },
    'reportParams.areaId': function(val, oldVal) {
      // get the sites for the selected branch
      // clear it out first
      this.$data.sites = [];
      this.$data.sites.push({value:0, text: 'All Sites'});
      this.$data.reportParams.siteId = 0;

      if (parseInt(val) > 0) {
        getSiteList(this.$http, val)
          .then((sitesList) => {
            sitesList.map((item) => {
              this.$data.sites.push(item);
            });
            this.$data.sitesLoaded = true;
            if (this.$data.userAuthLevel == 'Site Users') {
              this.$data.reportParams.siteId = this.$data.userDetails.site_id;
            }
          })
          .catch ((err) => {
            console.error(err);
          });
      }
    },
    'reportParams.siteId': function(val, oldVal) {
      this.$data.reportParams.siteId = val;
    },
    'reportParams.dateFrom': function(val, oldVal) {
      // check to make sure this date is not more than 3 months previous to the 'date to'
      const dateDiff = moment(val).diff(this.$data.reportParams.dateTo, 'months', true);
      console.log(dateDiff);
      if (dateDiff < -3) {
        this.$data.invalidDateRange = true;
      }
      else {
        this.$data.invalidDateRange = false;
        if (dateDiff > 0) {
          this.$data.dateInFutureError = true;
        }
        else {
          this.$data.dateInFutureError = false;
        }
      }
    },
    'reportParams.dateTo': function(val, oldVal) {
      // check to make sure this date is not more than 3 months after to the 'date from'
      const dateDiff = moment(val).diff(this.$data.reportParams.dateFrom, 'months', true);
      if (dateDiff > 3) {
        this.$data.invalidDateRange = true;
      }
      else {
        this.$data.invalidDateRange = false;
        if (dateDiff < 0) {
          this.$data.dateInFutureError = true;
        }
        else {
          this.$data.dateInFutureError = false;
        }
      }
    },
  },
  computed: {
    isMinimumSuperAdmin() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      
      return returnVal;
    },
    isMinimumCompanyUser() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Company Users':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      return returnVal;
    },
    isMinimumAreaUser() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Company Users':
        case 'Area Users':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      return returnVal;
    },
    isMinimumSiteUser() {
      let returnVal = false;
      switch (this.$data.userAuthLevel) {
        case 'Super Admin':
        case 'Company Users':
        case 'Area Users':
        case 'Site Users':
        case 'Read Only':
          returnVal = true;
          break;
        default:
          returnVal = false;
      }
      
      return returnVal;
    }
  },
  methods: {
    setUIForCurrentUser() {
      getCurrentUser()
      .then((userDetails) => {
        this.$data.userAuthLevel = userDetails.auth;
        this.$data.reportParams.companyId = userDetails.company_id;
        this.$data.reportParams.areaId = userDetails.area_id;
        this.$data.reportParams.siteId = userDetails.site_id;
        console.log(this.$data.reportParams);
      })
    },
    getInitialData() {
      getCurrentUser()
        .then((userDetails) => {
          this.$data.userDetails = userDetails;
          this.$data.userAuthLevel = userDetails.auth;
          this.$data.reportParams.companyId = userDetails.company_id;
          this.$data.reportParams.areaId = userDetails.area_id;
          this.$data.reportParams.siteId = userDetails.site_id;
          return getCompaniesList(this.$http);
        })
        .then((results) => {
          this.$data.companies = results;
          this.$data.companiesLoaded = true;
        });
    },
    setResultsTarget(reportOutput) {
      this.$data.reportFetchStarted = false;
      reportOutput == 'screen' ? this.$data.reportOutput = 'download' : this.$data.reportOutput = 'screen';
    },
    gotoUserReports() {
      this.$router.push('/reports/user-report/repeat-reason-report');
    },
    getClassForResultsTarget(reportOutput) {
        return reportOutput == 'screen' ? 'outline-primary' : 'outline-download';
    },
    async runReport() {
      this.$data.reportFetchStarted = true;
      let ukReportData = [];
      this.$data.reportItems=[];

      if (this.$data.reportOutput == 'screen') {

        this.$data.resultsLoaded = false;

        // get the uk plate data
        if (this.$data.reportParams.reportCountryType != 'eire') {
          const ukReportDataRaw = await fetchReportData(this.$http, this.$data.reportParams);
          if(ukReportDataRaw.plates) {
            ukReportData = ukReportDataRaw.plates.map((item) => {
              if (moment(item.printed_date).isDST()) {
                item.formatted_date = moment(item.printed_date).add(1, 'h').format('lll');
                item.printed_date = moment(item.printed_date).add(1, 'h');
              }
              else {
                item.formatted_date = moment(item.printed_date).format('lll');
                item.printed_date = moment(item.printed_date).add(1, 'h')
              }
              // fill in the fields that don't apply to the uk data
              item.index_mark_code = 'N/A';
              item.index_mark_irish_name = 'N/A';
              item.country_type = 'UK';
              return item;
            });
          }
        }

        this.$data.reportItems = ukReportData;

        // sort the results ready for the csv export
        this.$data.exportReportData = this.$data.reportItems.map((plateItem) => {
          return {
            "Country": plateItem.country_type,
            "Company Name": plateItem.company.company_name,
            "Area Name": plateItem.area.area_name,
            "Site Name": plateItem.site.site_name,
            "Account Code": plateItem.account_code,
            "Registration Number": plateItem.registration_number,
            "Slogan": plateItem.slogan,
            "Postcode": plateItem.postcode,
            "WIP Number": plateItem.supplier_wip_number,
            "Reprint Reason": plateItem.reprint_reason,
            "Plate Type": plateItem.plate_code.replace(/(^\w{1})|(\s{1}\w{1})/g, match => match.toUpperCase()),
            "Printed By": plateItem.printed_by_name,
            "Printed Date": '"' + moment(plateItem.printed_date).format("LL") + '"',
            "Printed Time": moment(plateItem.printed_date).format("LTS"),
            "Template Used": (plateItem.template ? plateItem.template.template_name : ''),
            "POID Description": plateItem.customer ? plateItem.customer.legal_doc.document_name : 'N/A',
            "POID Reference": plateItem.customer ? plateItem.customer.proof_id_ref : 'N/A',
            "POE Description": plateItem.customer ? plateItem.customer.legal_doc_ent.document_name : 'N/A',
            "POE Reference": plateItem.customer ? plateItem.customer.proof_ent_ref : 'N/A',
            "Customer Name": (plateItem.customer ? plateItem.customer.contacts[0].first_name + ' ' + plateItem.customer.contacts[0].last_name : ''),
            "Customer Address": plateItem.customer ? plateItem.customer.addresses[0].property_number.replace(/,/g, "") + ' ' + plateItem.customer.addresses[0].address1.replace(/,/g, "") + ' ' + plateItem.customer.addresses[0].town.replace(/,/g, "") + ' ' + plateItem.customer.addresses[0].county.county_name + ' ' + plateItem.customer.addresses[0].post_code.replace(/,/g, "") + ' ' + plateItem.customer.addresses[0].country.country_name : 'N/A',
            "Customer Phone": plateItem.customer ? plateItem.customer.contacts[0].phone_number : 'N/A',
            "Customer Email": plateItem.customer ? plateItem.customer.contacts[0].email: 'N/A'
          }
        });

        this.$data.resultsLoaded = true;

      }
      else {
        // The background report
        try {
          const backgroundReportProcess = await fetchBackgroundReportData(this.$http, this.$data.reportParams);
          console.log(backgroundReportProcess.success);
          if (backgroundReportProcess.success != true) {
            this.$data.reportBackgroundStatus = 'error';
          }
          else {
            this.$data.reportBackgroundStatus = 'success';
          }
        }
        catch (e) {
          this.$data.reportBackgroundStatus = 'error';
        }
      }

    },
    csvExport() {
      //console.log('export: ', this.$data.exportReportData);
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += [
        Object.keys(this.$data.exportReportData[0]).join(","),
        ...this.$data.exportReportData.map(item => Object.values(item).join(","))
      ]
        .join("\n")
        .replace(/(^\[)|(\]$)/gm, "");

      const data = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", "export.csv");
      link.click();
    }
  }
}
</script>

<style scoped>
  .uppercase {
    text-transform: uppercase;
  }
  .btn-outline-download {
    color: #c69500!important;
    border-color: #c69500!important;
  }
  .download-link {
    color: #c69500!important;
  }
  .background-report-output {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .toggle-option-button {
    margin-top: -5px;
  }
  .col-right {
    text-align: right;
  }
</style>
